import React, { useState, useEffect, useMemo } from 'react';
import { Box, Text, VStack, Image, HStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { USER_TYPES, APPLICATION_STATUS, EVENT_STATUS } from '../constants';
import { sendLog, sendError, convertTimestampToTimeString, getCachedInfluencerApplications, filterRelevantApplications, clearInfluencerApplicationsCache } from '../utils';

const MyEventsList: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useUser();
  const [applications, setApplications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch applications data
  useEffect(() => {
    const fetchApplications = async () => {
      if (!config?.apiUrl || !user?.id || user.userType !== USER_TYPES.INFLUENCER) {
        return;
      }

      setIsLoading(true);
      try {
        // Always clear cache to ensure fresh data (especially after cancellations)
        clearInfluencerApplicationsCache();
        
        const applicationsData = await getCachedInfluencerApplications(config.apiUrl, user.id);
        
        // Debug: Log all application statuses
        console.log('MyEventsList - All applications:', applicationsData.map((app: any) => ({
          id: app.id,
          status: app.status,
          eventId: app.eventId
        })));
        
        setApplications(applicationsData);
        
        sendLog('MyEventsListLoadSuccess', {
          influencerId: user.id,
          applicationCount: applicationsData.length
        });
      } catch (error: any) {
        const errorToLog = error instanceof Error 
          ? error 
          : new Error(error.message || 'Failed to fetch applications');
        
        sendError(errorToLog, {
          context: 'MyEventsList.fetchApplications',
          influencerId: user.id,
          status: error.response?.status
        });
        
        setApplications([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
    
    // Also refresh when component becomes visible (user navigates back)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchApplications();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [config?.apiUrl, user?.id, user?.userType]);

  // Filter and transform applications
  const events = useMemo(() => {
    // Filter for relevant applications (time-wise)
    const relevantApps = filterRelevantApplications(applications);
    
    return relevantApps.map((app: any) => {
      const event = app.event;
      
      if (!event) {
        return null;
      }

      // Extract date from startTime timestamp
      let parsedDate: Date | null = null;
      let dateString = '';
      let dayString = '';
      
      if (event.startTime) {
        parsedDate = new Date(event.startTime);
        if (!isNaN(parsedDate.getTime())) {
          dateString = parsedDate.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
          dayString = parsedDate.toLocaleDateString('en-US', { weekday: 'short' });
        }
      }

      // Convert timestamp to time string
      const timeString = convertTimestampToTimeString(event.startTime);
      
      // Get event image from photos array
      const eventImage = event.photos && event.photos.length > 0 
        ? event.photos[0] 
        : event.data?.photos?.[0] || '';
      
      // Determine status label and color
      // Priority: Event cancelled > Application cancelled > Other application statuses
      let status = '';
      let statusColor = '';
      let statusTextColor = '#FFFFFF';
      
      // Debug: Log the actual status values
      console.log('MyEventsList - Event status:', event.status, 'Application status:', app.status);
      console.log('MyEventsList - EVENT_STATUS.CANCELLED:', EVENT_STATUS.CANCELLED, 'APPLICATION_STATUS.CANCELLED:', APPLICATION_STATUS.CANCELLED);
      
      // If event is cancelled, show "Cancelled" regardless of application status
      if (event.status === EVENT_STATUS.CANCELLED) {
        status = 'Cancelled';
        statusColor = '#F44336'; // Red background for cancelled
      } else if (app.status === APPLICATION_STATUS.CANCELLED || app.status?.toLowerCase() === 'cancelled') {
        // Check for cancelled application status before other statuses
        // Also check lowercase in case of case mismatch
        status = 'Cancelled';
        statusColor = '#F44336'; // Red background for cancelled
      } else if (app.status === APPLICATION_STATUS.PENDING || app.status?.toLowerCase() === 'pending') {
        status = 'Pending';
        statusColor = '#FFA500';
      } else if (app.status === APPLICATION_STATUS.APPROVED || app.status?.toLowerCase() === 'approved') {
        status = 'Approved';
        statusColor = '#4CAF50';
      } else if (app.status === APPLICATION_STATUS.REJECTED || app.status?.toLowerCase() === 'rejected') {
        status = 'Rejected';
        statusColor = '#F44336';
      }
      
      return {
        id: event.id,
        name: event.eventName,
        date: dateString,
        day: dayString,
        time: timeString,
        location: event.data?.eventLocation || event.eventLocation || '',
        image: eventImage,
        status,
        statusColor,
        statusTextColor
      };
    }).filter(Boolean); // Remove null entries
  }, [applications]);

  // Check if loading or events list is empty
  if (isLoading) {
    return (
      <Box paddingTop="20px" textAlign="center">
        <Text className="static-text">Loading applications...</Text>
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box paddingTop="20px" >
        <Text
          className="subtitle-text-center"
          whiteSpace="pre-line"
        >
          0 events{'\n'}Pending for approval
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing="14px" align="stretch" width="100%">
        {events.map((event: any) => (
          <Box
            className="vstack-main-box"
            key={event.id}
            height="275px"
            onClick={() => navigate(`/event/${event.id}`)}
          >
            {/* Event Image */}
            <Box
              className="event-image-box"
              right="0"
            >
              <Image
                className="event-image-small"
                src={event.image}
                alt={event.name}
              />
            </Box>

            {/* Status Label - positioned at top right */}
            {event.status && (
              <Box
                position="absolute"
                top="12px"
                right="12px"
                className="event-status-label"
                backgroundColor={event.statusColor}
                color={event.statusTextColor}
                zIndex="10"
              >
                {event.status}
              </Box>
            )}

            {/* Date Badge */}
            <Box className="event-badge-box" >
              <Text className="item-badge-text" >
                {event.date ? (event.date.includes(' ') ? event.date.split(' ')[0] : event.date) : ''}
              </Text>
              <Text
                className="subtitle-text"
                color="#1D1D1D"
              >
                {event.day || ''}
              </Text>
            </Box>

            {/* Event Details */}
            <Box className="event-big-details-box" >
              <Text
                className="item-event-name-text"
                marginBottom="8px"
              >
                {event.name}
              </Text>

              <HStack spacing="12px" alignItems="center" marginBottom="4px">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M6.5 0C2.91 0 0 2.91 0 6.5C0 10.09 2.91 13 6.5 13C10.09 13 13 10.09 13 6.5C13 2.91 10.09 0 6.5 0ZM6.5 9.75C4.29 9.75 2.5 7.96 2.5 5.75C2.5 3.54 4.29 1.75 6.5 1.75C8.71 1.75 10.5 3.54 10.5 5.75C10.5 7.96 8.71 9.75 6.5 9.75Z"
                    fill="#FFFFFF"
                  />
                  <path
                    d="M6.5 3.25C5.67 3.25 5 3.92 5 4.75C5 5.58 5.67 6.25 6.5 6.25C7.33 6.25 8 5.58 8 4.75C8 3.92 7.33 3.25 6.5 3.25Z"
                    fill="#FFFFFF"
                  />
                </svg>
                <Text className="static-text" >
                  {event.time}
                </Text>
              </HStack>

              <HStack spacing="12px" alignItems="center">
                <svg width="10" height="13" viewBox="0 0 10 13" fill="none">
                  <path
                    d="M5 0C2.24 0 0 2.24 0 5C0 8.5 5 13 5 13C5 13 10 8.5 10 5C10 2.24 7.76 0 5 0ZM5 6.5C4.17 6.5 3.5 5.83 3.5 5C3.5 4.17 4.17 3.5 5 3.5C5.83 3.5 6.5 4.17 6.5 5C6.5 5.83 5.83 6.5 5 6.5Z"
                    fill="#FFFFFF"
                  />
                </svg>
                <Text className="static-text" >
                  {event.location}
                </Text>
              </HStack>
            </Box>
          </Box>
        ))}
    </VStack>
  );
};

export default MyEventsList;
